import React from 'react';
import { Product } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../hooks/useCurrency';
import { Eye, Edit, Trash2, CheckSquare, Square } from 'lucide-react';

interface ProductTableProps {
  products: Product[];
  onEdit?: (product: Product) => void;
  onDelete?: (id: number) => void;
  onSelect?: (product: Product) => void;
  selectedProducts?: Product[];
  onSelectAll?: () => void;
  onPreview?: (product: Product) => void;
  loading?: boolean;
}

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  onEdit,
  onDelete,
  onSelect,
  selectedProducts = [],
  onSelectAll,
  onPreview,
  loading = false
}) => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();

  const isSelected = (product: Product) => {
    return selectedProducts.some(selected => selected.id === product.id);
  };

  const getStockStatusColor = (stockQuantity: number, minStockLevel: number) => {
    const stock = Number(stockQuantity);
    const minLevel = Number(minStockLevel);
    
    if (stock <= 0) return 'text-red-600';
    if (stock <= minLevel) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStatusBadgeColor = (status: Product['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'inactive': return 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-400';
    }
  };

  const isExpiringSoon = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    return expiry < today;
  };

  if (loading) {
    return (
      <div className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left">
                  <div className={`h-4 w-4 rounded animate-pulse ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className={`h-3 w-16 rounded animate-pulse ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className={`h-3 w-16 rounded animate-pulse ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className={`h-3 w-12 rounded animate-pulse ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className={`h-3 w-14 rounded animate-pulse ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className={`h-3 w-12 rounded animate-pulse ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden sm:table-cell ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className={`h-3 w-14 rounded animate-pulse ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className={`h-3 w-16 rounded animate-pulse ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${theme === 'dark' ? 'bg-gray-900 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {Array.from({ length: 5 }).map((_, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="px-2 sm:px-4 py-2 sm:py-3">
                    <div className={`h-4 w-4 rounded animate-pulse ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`}></div>
                  </td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3">
                    <div className="space-y-2">
                      <div className={`h-4 w-32 rounded animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`}></div>
                      <div className={`h-3 w-20 rounded animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`}></div>
                    </div>
                  </td>
                  <td className={`px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell`}>
                    <div className={`h-3 w-24 rounded animate-pulse ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`}></div>
                  </td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3">
                    <div className={`h-4 w-20 rounded animate-pulse ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`}></div>
                  </td>
                  <td className={`px-2 sm:px-4 py-2 sm:py-3 hidden lg:table-cell`}>
                    <div className={`h-3 w-24 rounded animate-pulse ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`}></div>
                  </td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3">
                    <div className="space-y-1">
                      <div className={`h-4 w-20 rounded animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`}></div>
                      <div className={`h-3 w-24 rounded animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`}></div>
                    </div>
                  </td>
                  <td className={`px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell`}>
                    <div className={`h-6 w-16 rounded-full animate-pulse ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`}></div>
                  </td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3">
                    <div className="flex items-center space-x-2">
                      <div className={`w-8 h-8 rounded animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`}></div>
                      <div className={`w-8 h-8 rounded animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`}></div>
                      <div className={`w-8 h-8 rounded animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`}></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className={`border rounded-lg ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-col items-center justify-center h-64">
          <svg className={`w-12 h-12 mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            No Products found
          </p>
          <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
            Add a new Product to get started
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
            <tr>
              {onSelect && (
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left">
                  <button
                    onClick={onSelectAll}
                    className="flex items-center touch-manipulation"
                  >
                    {selectedProducts.length === products.length && products.length > 0 ? (
                      <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    ) : (
                      <Square className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    )}
                  </button>
                </th>
              )}
              <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Product
              </th>
              <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Category
              </th>
              <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Stock
              </th>
              <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Expiry
              </th>
              <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Price
              </th>
              <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden sm:table-cell ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Status
              </th>
              <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${theme === 'dark' ? 'bg-gray-900 divide-gray-700' : 'bg-white divide-gray-200'}`}>
            {products.map((product) => (
              <tr 
                key={product.id}
                className={`hover:${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} transition-colors ${
                  isSelected(product) ? (theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50') : ''
                }`}
              >
                {onSelect && (
                  <td className="px-2 sm:px-4 py-2 sm:py-3">
                    <button
                      onClick={() => onSelect(product)}
                      className="flex items-center touch-manipulation"
                    >
                      {isSelected(product) ? (
                        <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      ) : (
                        <Square className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                      )}
                    </button>
                  </td>
                )}
                
                <td className="px-2 sm:px-4 py-2 sm:py-3">
                  <div>
                    <div className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {product.name}
                    </div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {product.brand}
                    </div>
                    {/* Show category on mobile */}
                    <div className={`text-xs md:hidden mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {product.category}
                    </div>
                  </div>
                </td>
                
                <td className={`px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm hidden md:table-cell ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                  {product.category}
                </td>
                
                <td className="px-2 sm:px-4 py-2 sm:py-3">
                  <div>
                    {(() => {
                      const stockQty = Number(product.stock_quantity);
                      const isOutOfStock = stockQty === 0 || stockQty < 0;
                      
                      if (isOutOfStock) {
                        return (
                          <span className="text-xs sm:text-sm font-medium text-red-600">
                            Out of Stock
                          </span>
                        );
                      } else {
                        return (
                          <span className={`text-xs sm:text-sm font-medium ${getStockStatusColor(product.stock_quantity, product.minimum_stock_level)}`}>
                            {product.stock_quantity} {product.unit}
                          </span>
                        );
                      }
                    })()}
                  </div>
                  {(() => {
                    const stockQty = Number(product.stock_quantity);
                    const isOutOfStock = stockQty === 0 || stockQty < 0;
                    
                    if (!isOutOfStock && product.stock_quantity <= product.minimum_stock_level) {
                      return <div className="text-xs text-red-600 font-medium mt-1">Low Stock</div>;
                    }
                    return null;
                  })()}
                </td>
                
                <td className="px-2 sm:px-4 py-2 sm:py-3 hidden lg:table-cell">
                  {product.expiry_date ? (
                    <>
                      <div className={`text-xs sm:text-sm ${
                        isExpired(product.expiry_date) 
                          ? 'text-red-600 font-medium' 
                          : isExpiringSoon(product.expiry_date) 
                            ? 'text-yellow-600 font-medium' 
                            : theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                      }`}>
                        {new Date(product.expiry_date).toLocaleDateString()}
                      </div>
                      {isExpired(product.expiry_date) && (
                        <div className="text-xs text-red-600 font-bold">EXPIRED</div>
                      )}
                      {isExpiringSoon(product.expiry_date) && !isExpired(product.expiry_date) && (
                        <div className="text-xs text-yellow-600 font-bold">EXPIRING</div>
                      )}
                    </>
                  ) : (
                    <div className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>N/A</div>
                  )}
                </td>
                
                <td className="px-2 sm:px-4 py-2 sm:py-3">
                  <div className={`text-xs sm:text-sm font-medium text-green-600`}>
                    {formatCurrency(product.selling_price)}
                  </div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Purchase: {formatCurrency(product.purchase_price)}
                  </div>
                </td>
                
                <td className="px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">
                  <span className={`inline-flex items-center px-1.5 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(product.status)}`}>
                    {product.status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </span>
                </td>
                
                <td className="px-2 sm:px-4 py-2 sm:py-3">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    {onPreview && (
                      <button
                        onClick={() => onPreview(product)}
                        disabled={loading}
                        className={`text-blue-600 hover:text-blue-800 transition-colors p-1 rounded ${
                          loading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title="Preview Product"
                      >
                        <Eye className={`w-4 h-4 ${loading ? 'animate-pulse' : ''}`} />
                      </button>
                    )}
                    
                    {onEdit && (
                      <button
                        onClick={() => onEdit(product)}
                        disabled={loading}
                        className={`text-yellow-600 hover:text-yellow-800 transition-colors p-1 rounded ${
                          loading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title="Edit Product"
                      >
                        <Edit className={`w-4 h-4 ${loading ? 'animate-pulse' : ''}`} />
                      </button>
                    )}
                    
                    {onDelete && (
                      <button
                        onClick={() => onDelete(product.id)}
                        disabled={loading}
                        className={`text-red-600 hover:text-red-800 transition-colors p-1 rounded ${
                          loading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title="Delete Product"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductTable;